import React, { createContext, useContext, useEffect, useState } from 'react';

export interface CartItem {
  productId: number;
  variationId?: number;
  quantity: number;
  name: string;
  price: number;
  primaryImage: string;
  customDetails?: string;
}

interface CartContextType {
  cartItems: CartItem[];
  total: number;
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: number, variationId?: number) => void;
  updateQuantity: (productId: number, variationId: number | undefined, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType>({} as CartContextType);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [hasHydrated, setHasHydrated] = useState(false);

  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('cart');
      if (stored) {
        setCartItems(JSON.parse(stored));
      }
    } catch (err) {
      console.warn('Failed to parse cart from localStorage', err);
    } finally {
      setHasHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (hasHydrated) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, hasHydrated]);

  const addToCart = (newItem: CartItem) => {
    setCartItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          item.productId === newItem.productId &&
          item.variationId === newItem.variationId
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += newItem.quantity;
        return updated;
      }
      return [...prev, newItem];
    });
  };

  const removeFromCart = (productId: number, variationId?: number) => {
    setCartItems((prev) =>
      prev.filter(
        (item) =>
          item.productId !== productId || item.variationId !== variationId
      )
    );
  };

  const updateQuantity = (productId: number, variationId: number | undefined, quantity: number) => {
    setCartItems((prev) => {
      const updated = [...prev];
      const idx = updated.findIndex(
        (item) =>
          item.productId === productId && item.variationId === variationId
      );
      if (idx >= 0 && quantity > 0) {
        updated[idx].quantity = quantity;
      } else if (idx >= 0 && quantity <= 0) {
        updated.splice(idx, 1);
      }
      return updated;
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider value={{ cartItems, total, addToCart, removeFromCart, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
